// Admin Shop → Orders: who bought what, how it reaches them (delivery address
// or pickup at an event), and its fulfilment status.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage } from './_shared';

const STATUS = {
  pending: { label: 'To fulfil', cls: 'bg-amber-50 text-amber-700' },
  needs_details: { label: 'Needs details', cls: 'bg-red-50 text-red-600' },
  shipped: { label: 'Shipped', cls: 'bg-blue-50 text-blue-700' },
  delivered: { label: 'Delivered', cls: 'bg-green-50 text-green-700' },
  ready_for_pickup: { label: 'Ready for pickup', cls: 'bg-blue-50 text-blue-700' },
  collected: { label: 'Collected', cls: 'bg-green-50 text-green-700' },
};
const NEXT = {
  delivery: ['pending', 'shipped', 'delivered'],
  pickup: ['pending', 'ready_for_pickup', 'collected'],
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function addressText(f) {
  return [f.line1, f.line2, f.landmark, `${f.city}, ${f.state} ${f.pincode}`].filter(Boolean).join(', ');
}

export default function AdminOrdersTab() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState('open');   // open | all
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setOrders((await axiosInstance.get('/admin/orders')).data || []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load orders'));
      setOrders([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (order, fulfilment_status) => {
    let note;
    if (fulfilment_status === 'shipped') {
      note = window.prompt('Courier and tracking number (optional)', order.fulfilment?.admin_note || '');
      if (note === null) return;
    }
    setBusy(order.id);
    try {
      const res = await axiosInstance.patch(`/admin/orders/${order.id}/fulfilment`, { fulfilment_status, note });
      setOrders((list) => list.map((o) => (o.id === order.id ? { ...o, fulfilment: res.data.fulfilment } : o)));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update the order'));
    } finally {
      setBusy(null);
    }
  };

  if (orders === null) {
    return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E54562]" /></div>;
  }
  const done = (o) => ['delivered', 'collected'].includes(o.fulfilment?.fulfilment_status);
  const shown = orders.filter((o) => o.status === 'paid' && (filter === 'all' || !done(o)));

  return (
    <div className="space-y-3" data-testid="admin-orders">
      <div className="flex items-center gap-2">
        {[['open', 'To fulfil'], ['all', 'All paid orders']].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === id ? 'bg-[#2B3A67] text-white' : 'bg-white border border-gray-200 text-gray-500'}`}>
            {label}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs font-bold text-[#2B3A67]"><i className="ph-bold ph-arrow-clockwise mr-1" />Refresh</button>
      </div>

      {shown.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No orders here.</p>}

      {shown.map((o) => {
        const f = o.fulfilment || {};
        const st = STATUS[f.fulfilment_status] || (f.method ? STATUS.pending : { label: 'No delivery details', cls: 'bg-gray-100 text-gray-500' });
        return (
          <div key={o.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-2" data-testid={`order-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-sm">{o.customer?.name || 'Unknown customer'}</p>
                <p className="text-xs text-gray-400">
                  {o.customer?.mobile_number || 'no mobile'} · {o.customer?.email}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  #{o.id.slice(0, 8)} · {o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="font-extrabold">{inr(o.total_amount)}</p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
              </div>
            </div>

            <ul className="text-xs text-gray-600 space-y-0.5">
              {o.items.map((it) => (
                <li key={it.id || it.product_id}>{it.quantity} × {it.product_name}{it.variant_details ? ` (${it.variant_details})` : ''}</li>
              ))}
            </ul>

            <div className="text-xs bg-gray-50 rounded-lg p-2.5">
              {f.method === 'delivery' ? (
                <>
                  <p className="font-bold text-gray-700"><i className="ph-bold ph-truck mr-1" />Deliver to {f.full_name} · {f.phone}</p>
                  <p className="text-gray-600 mt-0.5">{addressText(f)}</p>
                </>
              ) : f.method === 'pickup' ? (
                <p className="font-bold text-gray-700"><i className="ph-bold ph-storefront mr-1" />Collect at an event{f.note ? `: ${f.note}` : ''}</p>
              ) : (
                <p className="text-gray-500">No delivery details on this order — contact the customer.</p>
              )}
              {f.admin_note && <p className="text-gray-500 mt-1"><i className="ph-bold ph-note mr-1" />{f.admin_note}</p>}
            </div>

            {f.method && (
              <div className="flex gap-1.5 flex-wrap">
                {NEXT[f.method].map((s) => (
                  <button key={s} disabled={busy === o.id || f.fulfilment_status === s} onClick={() => setStatus(o, s)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                      f.fulfilment_status === s ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200 text-gray-600'}`}>
                    {STATUS[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
